import type { ISupportedLanguagesAndLocale } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import { getLocaleForScrapingConfig } from "app/components/campaigns/shared/CampaignsUtils";
import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { URL_DEVICE_TO_LOCATION_DEVICE } from "./constants";
import type { LocationOption } from "./types";

// API returns descriptions like "English - en"; we only need the short part after " - "
const LANGUAGE_DESCRIPTION_SEPARATOR = " - ";

/** When device is BOTH, appends the device to avoid duplicate IDs for same-locale configs. */
export const getLocationOptionId = (config: ICampaignsScrapingConfiguration, device: LocationDevice): string => {
    const baseId = getLocaleForScrapingConfig(config);
    return device === "BOTH" ? `${baseId}_${config.device}` : baseId;
};

/** API uses "Desktop"/"Mobile" on config.device; modal uses "DESKTOP"/"MOBILE"/"BOTH" */
export const getDeviceFilterFn = (device: LocationDevice) => {
    if (device === "BOTH") {
        return () => true;
    }
    const apiDevice = device === "DESKTOP" ? DEVICES_TYPES_FOR_API.DESKTOP : DEVICES_TYPES_FOR_API.MOBILE;
    return (config: ICampaignsScrapingConfiguration) => config.device.toString() === apiDevice;
};

/** Filters scraping configs by device and builds location dropdown options. */
export const buildLocationOptions = (
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
    device: LocationDevice,
    languagesList: ISupportedLanguagesAndLocale[],
): LocationOption[] => {
    return scrapingConfigurations.filter(getDeviceFilterFn(device)).map((config) => {
        const language = languagesList?.find((lang) => lang.locale === config.locale);
        const shortLanguage = language?.description.includes(LANGUAGE_DESCRIPTION_SEPARATOR)
            ? language.description.split(LANGUAGE_DESCRIPTION_SEPARATOR)[1]
            : language?.description;

        return {
            id: getLocationOptionId(config, device),
            text: `${config.geoLocation} (${shortLanguage}, ${config.device})`,
            countryIconID: config.geoLocationCountry,
        };
    });
};

/** Returns available device options in display order: BOTH (if applicable), DESKTOP, MOBILE. */
export const getDistinctDevices = (scrapingConfigurations: ICampaignsScrapingConfiguration[]): LocationDevice[] => {
    const deviceSet = new Set(scrapingConfigurations?.map((config) => config.device.toString()));
    const options: LocationDevice[] = [];

    const hasDesktop = deviceSet.has(DEVICES_TYPES_FOR_API.DESKTOP);
    const hasMobile = deviceSet.has(DEVICES_TYPES_FOR_API.MOBILE);

    if (hasDesktop && hasMobile) {
        options.push("BOTH");
    }
    if (hasDesktop) {
        options.push("DESKTOP");
    }
    if (hasMobile) {
        options.push("MOBILE");
    }
    return options;
};

/** Maps the URL `device` param to a LocationDevice, falls back to first available. */
export const resolveInitialDevice = (
    urlDevice: string | undefined,
    availableDevices: LocationDevice[],
): LocationDevice => {
    const mapped = urlDevice ? URL_DEVICE_TO_LOCATION_DEVICE[urlDevice] : undefined;
    if (mapped && availableDevices.includes(mapped)) {
        return mapped;
    }
    return availableDevices[0] ?? "DESKTOP";
};
